import React, { useEffect } from "react";
import useStore from "../../../store/useStore";
import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";
import CardGiftcardOutlinedIcon from "@mui/icons-material/CardGiftcardOutlined";
import AutorenewOutlinedIcon from "@mui/icons-material/AutorenewOutlined";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";

// ── Helpers ────────────────────────────────────────────────────────────────

const formatDate = (dateStr) => {
  if (!dateStr) return "—";
  try {
    return new Intl.DateTimeFormat("en-IN", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }).format(new Date(dateStr));
  } catch {
    return dateStr;
  }
};

const formatAmount = (amount) =>
  `₹${Math.abs(Number(amount ?? 0)).toFixed(2)}`;

// Determine if a transaction is credit (+) or debit (-)
const isCredit = (txn) => {
  const type = String(txn.type ?? txn.transaction_type ?? "").toLowerCase();
  const amt = Number(txn.amount ?? 0);
  if (type === "credit" || type === "cashback" || type === "referral" || type === "refund")
    return true;
  if (type === "debit" || type === "order" || type === "purchase") return false;
  // fallback: positive amount = credit
  return amt >= 0;
};

const txnIcon = (txn) => {
  const type = String(txn.type ?? txn.transaction_type ?? "").toLowerCase();
  if (type.includes("referral")) return CardGiftcardOutlinedIcon;
  if (type.includes("refund") || type.includes("cashback")) return AutorenewOutlinedIcon;
  return ShoppingCartOutlinedIcon;
};

const txnLabel = (txn) => {
  const raw = String(txn.type ?? txn.transaction_type ?? "").toLowerCase();
  const desc = txn.description ?? txn.note ?? txn.remarks ?? "";
  if (desc) return desc;
  const map = {
    credit: "Wallet Credit",
    debit: "Wallet Debit",
    cashback: "Cashback",
    refund: "Refund",
    referral: "Referral Reward",
    order: "Order Payment",
    purchase: "Purchase Deduction",
  };
  return map[raw] ?? "Transaction";
};

// ── Skeleton loader ──────────────────────────────────────────────────────────

const SkeletonRow = () => (
  <div className="flex items-center gap-4 px-5 py-4 border-b border-gray-100 last:border-b-0 animate-pulse">
    <div className="h-10 w-10 shrink-0 rounded-full bg-gray-100" />
    <div className="flex-1 space-y-2">
      <div className="h-3 w-40 rounded bg-gray-100" />
      <div className="h-2.5 w-24 rounded bg-gray-100" />
    </div>
    <div className="h-4 w-16 rounded bg-gray-100" />
  </div>
);

// ── Main Component ───────────────────────────────────────────────────────────

const WalletSection = () => {
  const { wallet, wallet_loading, wallet_error, fetchWallet } = useStore();

  useEffect(() => {
    fetchWallet();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const balance = parseFloat(wallet?.balance ?? 0);
  const currency = wallet?.currency ?? "INR";
  const transactions = wallet?.transactions ?? [];

  // Derive summary stats from transactions
  const totalCredited = transactions
    .filter(isCredit)
    .reduce((s, t) => s + Math.abs(Number(t.amount ?? 0)), 0);
  const totalDebited = transactions
    .filter((t) => !isCredit(t))
    .reduce((s, t) => s + Math.abs(Number(t.amount ?? 0)), 0);

  return (
    <div>
      {/* Header */}
      <div className="mb-5 flex items-center justify-between border-b border-gray-200 pb-4">
        <div>
          <h1 className="text-lg font-semibold text-gray-900">My Wallet</h1>
          <p className="mt-0.5 text-sm text-gray-500">
            Your KisanFresh wallet balance and transaction history.
          </p>
        </div>
        <button
          onClick={fetchWallet}
          disabled={wallet_loading}
          title="Refresh"
          className="flex items-center gap-1.5 rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-semibold text-gray-600 hover:bg-gray-50 disabled:opacity-50 transition"
        >
          <RefreshOutlinedIcon
            style={{ fontSize: 15 }}
            className={wallet_loading ? "animate-spin" : ""}
          />
          Refresh
        </button>
      </div>

      {/* Balance card */}
      <div className="mb-5 overflow-hidden rounded-2xl bg-gradient-to-br from-[#046a38] via-[#057840] to-emerald-500 p-6 text-white shadow-lg shadow-emerald-900/20">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-sm font-medium text-emerald-100">
              Available Balance
            </p>
            {wallet_loading && !wallet ? (
              <div className="mt-2 h-9 w-36 animate-pulse rounded-lg bg-white/20" />
            ) : (
              <p className="mt-1 text-4xl font-extrabold tracking-tight">
                {currency === "INR" ? "₹" : currency}
                {balance.toFixed(2)}
              </p>
            )}
            <p className="mt-1 text-xs text-emerald-200">
              KisanFresh Wallet · {currency}
            </p>
          </div>
          <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-white/15 shadow-inner backdrop-blur-sm">
            <AccountBalanceWalletOutlinedIcon
              style={{ fontSize: 28 }}
              className="text-white"
            />
          </div>
        </div>

        {/* Stats row */}
        {!wallet_loading && (
          <div className="mt-5 grid grid-cols-2 gap-3">
            <div className="rounded-xl bg-white/10 px-4 py-3 backdrop-blur-sm">
              <div className="flex items-center gap-1.5 text-emerald-100">
                <TrendingUpIcon style={{ fontSize: 15 }} />
                <span className="text-xs font-medium">Total Credited</span>
              </div>
              <p className="mt-1 text-lg font-bold">₹{totalCredited.toFixed(2)}</p>
            </div>
            <div className="rounded-xl bg-white/10 px-4 py-3 backdrop-blur-sm">
              <div className="flex items-center gap-1.5 text-emerald-100">
                <TrendingDownIcon style={{ fontSize: 15 }} />
                <span className="text-xs font-medium">Total Debited</span>
              </div>
              <p className="mt-1 text-lg font-bold">₹{totalDebited.toFixed(2)}</p>
            </div>
          </div>
        )}
      </div>

      {/* Info banner */}
      <div className="mb-5 flex items-start gap-3 rounded-xl border border-emerald-100 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
        <span className="mt-0.5 shrink-0 text-base">💡</span>
        <p>
          Wallet balance is automatically applied at checkout when you toggle{" "}
          <strong>Use Wallet Balance</strong>. It cannot be transferred or
          withdrawn.
        </p>
      </div>

      {/* Transaction history */}
      <div className="rounded-xl border border-gray-200 bg-white overflow-hidden">
        <div className="flex items-center justify-between border-b border-gray-100 px-5 py-3.5">
          <h2 className="text-sm font-semibold text-gray-900">
            Transaction History
          </h2>
          {transactions.length > 0 && (
            <span className="rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-semibold text-gray-600">
              {transactions.length}
            </span>
          )}
        </div>

        {/* Loading */}
        {wallet_loading && !wallet && (
          <div>
            {[...Array(5)].map((_, i) => (
              <SkeletonRow key={i} />
            ))}
          </div>
        )}

        {/* Error */}
        {wallet_error && !wallet_loading && (
          <div className="px-5 py-10 text-center">
            <p className="text-sm font-medium text-red-500">{wallet_error}</p>
            <button
              onClick={fetchWallet}
              className="mt-3 rounded-lg bg-red-50 px-4 py-2 text-xs font-semibold text-red-600 hover:bg-red-100 transition"
            >
              Retry
            </button>
          </div>
        )}

        {/* Empty state */}
        {!wallet_loading && !wallet_error && transactions.length === 0 && (
          <div className="flex flex-col items-center justify-center px-5 py-14 text-center">
            <div className="mb-3 flex h-14 w-14 items-center justify-center rounded-2xl bg-gray-100 text-gray-400">
              <AccountBalanceWalletOutlinedIcon style={{ fontSize: 28 }} />
            </div>
            <p className="text-sm font-semibold text-gray-700">
              No transactions yet
            </p>
            <p className="mt-1 text-xs text-gray-400">
              Your wallet activity will appear here.
            </p>
          </div>
        )}

        {/* Transaction rows */}
        {!wallet_error && transactions.length > 0 && (
          <div className="divide-y divide-gray-50">
            {transactions.map((txn, idx) => {
              const credit = isCredit(txn);
              const Icon = txnIcon(txn);
              const id = txn.id ?? txn.transaction_id ?? idx;
              const date =
                txn.created_at ??
                txn.date ??
                txn.transaction_date ??
                txn.updated_at ??
                null;
              const orderId =
                txn.order_id ?? txn.reference_id ?? txn.reference ?? null;

              return (
                <div
                  key={id}
                  className="flex items-center gap-4 px-5 py-4 hover:bg-gray-50 transition-colors"
                >
                  {/* Icon */}
                  <div
                    className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${
                      credit
                        ? "bg-emerald-50 text-[#046a38]"
                        : "bg-red-50 text-red-500"
                    }`}
                  >
                    <Icon style={{ fontSize: 18 }} />
                  </div>

                  {/* Description */}
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-semibold text-gray-900">
                      {txnLabel(txn)}
                    </p>
                    <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5">
                      {date && (
                        <span className="text-xs text-gray-400">
                          {formatDate(date)}
                        </span>
                      )}
                      {orderId && (
                        <span className="text-xs text-gray-400">
                          · Order #{orderId}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Amount */}
                  <div className="shrink-0 text-right">
                    <span
                      className={`text-sm font-bold ${
                        credit ? "text-[#046a38]" : "text-red-500"
                      }`}
                    >
                      {credit ? "+" : "−"} {formatAmount(txn.amount)}
                    </span>
                    {txn.closing_balance != null && (
                      <p className="mt-0.5 text-[10px] text-gray-400">
                        Bal: ₹{Number(txn.closing_balance).toFixed(2)}
                      </p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default WalletSection;
